import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Radio,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Box,
  CircularProgress,
  Pagination
} from "@mui/material";
import PatternTable from "./PatternTable";
import { getSearchShoes } from "../services/api";

const Home = () => {
  // Default selection: Each column's 1st row is selected by default
  const [loading, setLoading] = useState(false);
  const defaultSelected = Array.from({ length: 10 }, () => 0); // Default to the 1st row for all columns
  const [selected, setSelected] = useState(defaultSelected);
  const [enabledColumns, setEnabledColumns] = useState(new Set([0, 1, 2, 3])); // Columns 1 to 4 are enabled by default
  const [searchResults, setSearchResults] = useState(null);
  // Pagination state
  const [page, setPage] = useState(1); // Current page
  const [pageSize, setPageSize] = useState(5); // Number of items per page

  const handleRadioChange = (column, rowIndex) => {
    if (enabledColumns.has(column)) {
      setSelected((prev) => {
        const newSelected = [...prev];
        newSelected[column] = rowIndex;
        return newSelected;
      });
    }
  };

  const handleColumnChange = (event) => {
    const value = event.target.value;
    const updatedEnabledColumns = new Set([0, 1, 2, 3]); // Keep columns 1 to 4 always enabled

    for (let i = 3; i < 10; i++) {
      if (i < value) {
        updatedEnabledColumns.add(i);
      }
    }

    setEnabledColumns(updatedEnabledColumns);
  };

  const generateSeriesString = (selected) => {
    const rowToCharacter = {
      1: "B",
      2: "P",
      3: "T",
    };

    return selected.map(({ row }) => rowToCharacter[row] || "").join(",");
  };

  const handleSearch = (pag=0) => {
    setLoading(true); // Start loader

    const selectedItems = selected
      .map((rowIndex, colIndex) => {
        if (enabledColumns.has(colIndex)) {
          return { column: colIndex + 1, row: rowIndex + 1 }; // Column and row are 1-based
        }
        return null; // Exclude disabled columns
      })
      .filter((item) => item !== null); // Remove null values

    const seriesString = generateSeriesString(selectedItems);
    let pageNo = page
    if(pag > 0) {
      pageNo = pag
    }
    // Pass pagination parameters to the API request
    getSearchShoes(seriesString, pageNo, pageSize)
      .then((response) => {
        setSearchResults(response); // Update the state with the received response
      })
      .catch((error) => {
        console.error("Error occurred while sending data:", error);
      })
      .finally(() => {
        setLoading(false); // Stop loader
      });
  };

  const rows = [
    { color: "error" }, // Row 1 (Red/Danger)
    { color: "primary" }, // Row 2 (Blue/Primary)
    { color: "success" }, // Row 3 (Green/Success)
  ];

  const handlePageChange = (event, newPage) => {
    setPage(newPage); // Update the page number
    handleSearch(newPage);    // Trigger the search again with the new page number
    window.scrollTo(0, 0);  // Scroll to the top of the page
  };

  // Handle page size change
  const handlePageSizeChange = (event) => {
    setPageSize(event.target.value);
  };

  const handleJumpToTop = (event) => {
    window.scrollTo(0, 0);  // Scroll to the top of the page
  };

  const handleSearchBtn = () => {
    setPage(1); // Update the page number
    handleSearch(1);    // Trigger the search again with the new page number
  }
  


  return (
    <div>
      <Typography variant="h5" gutterBottom align="center">
        Select a Radio Button from Each Column
      </Typography>

      <Box display="flex" justifyContent="center" gap={2} mb={2}>
        {/* Enable Columns Dropdown */}
        <FormControl style={{ minWidth: 400 }}>
          <InputLabel id="column-select-label">Enable Columns (4 to 10)</InputLabel>
          <Select
            labelId="column-select-label"
            value={enabledColumns.size}
            onChange={handleColumnChange}
            label="Enable Columns"
          >
            <MenuItem value={4}>Enable 4 columns (1 to 4)</MenuItem>
            <MenuItem value={5}>Enable 5 columns (1 to 5)</MenuItem>
            <MenuItem value={6}>Enable 6 columns (1 to 6)</MenuItem>
            <MenuItem value={7}>Enable 7 columns (1 to 7)</MenuItem>
            <MenuItem value={8}>Enable 8 columns (1 to 8)</MenuItem>
            <MenuItem value={9}>Enable 9 columns (1 to 9)</MenuItem>
            <MenuItem value={10}>Enable all columns</MenuItem>
          </Select>
        </FormControl>

        {/* Items Per Page Dropdown */}
        <FormControl style={{ minWidth: 400 }}>
          <InputLabel id="page-size-select-label">Items Per Page</InputLabel>
          <Select
            labelId="page-size-select-label"
            value={pageSize}
            onChange={handlePageSizeChange}
            label="Items Per Page"
          >
            <MenuItem value={5}>5</MenuItem>
            <MenuItem value={15}>15</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
            <MenuItem value={200}>200</MenuItem>
            <MenuItem value={300}>300</MenuItem>
          </Select>
        </FormControl>
      </Box>


      <TableContainer component={Paper} style={{ marginTop: "20px" }}>
        <Table style={{ backgroundColor: "#c7c7c1" }}>
          <TableHead>
            <TableRow>
              {Array.from({ length: 10 }, (_, i) => (
                <TableCell key={i} align="center">
                  {`${i + 1}TH`}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                {Array.from({ length: 10 }).map((_, colIndex) => (
                  <TableCell
                    key={colIndex}
                    align="center"
                    sx={{ verticalAlign: "middle",padding: '0' }} // Optional: Centers content vertically
                  >
                    <Radio
                      checked={selected[colIndex] === rowIndex}
                      onChange={() => handleRadioChange(colIndex, rowIndex)}
                      name={`column-${colIndex}`}
                      inputProps={{
                        "aria-label": `Row ${rowIndex + 1} Column ${colIndex + 1}`,
                      }}
                      color={row.color} // Set color based on the row
                      disabled={!enabledColumns.has(colIndex)} // Disable if column is not in the enabled set
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box display="flex" justifyContent="center" marginTop="20px" marginBottom="20px">
        <Button
          variant="contained"
          sx={{ 
            backgroundColor: "#495057", // Dark gray color
            color: "white", // Text color to ensure good contrast
            px: 4, 
            py: 2
           }}
          
          onClick={handleSearchBtn}
          disabled={loading} // Disable the button while loading
        >
          {loading ? (
            <CircularProgress size={24} style={{ color: 'white' }} />
          ) : (
            'Search'
          )}
        </Button>
      </Box>

      {searchResults &&
        Object.entries(searchResults.dataAll).map(([key, value], index) => (
          <PatternTable
            key={index}
            data={value}
            serCombinationColor={searchResults.serCombinationColorAll[key]}
            shoeDataAll={searchResults.shoeDataAll[key]}
            countCombination={searchResults.occuranceCount[key]}
          />
        ))
      }

      {searchResults && searchResults.totalPages > 1 && (
        <div  style={{position:'relative'}}>
          <Box display="flex" justifyContent="center" marginTop="20px">
            <Pagination
              count={searchResults.totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              shape="rounded"
            />
          </Box>
          <div
          onClick={handleJumpToTop}
          style={{
            position: 'fixed',
            right: '30px',
            cursor: 'pointer',
            bottom: '45px',
            fontSize: 'large',
            color: 'red',
          }}
          >^ Jump To Top ^</div>

          <div
          onClick={handleJumpToTop}
          style={{
            position: 'fixed',
            right: '30px',
            cursor: 'pointer',
            bottom: '25px',
            color: 'blue',
          }}
          >Total Shoes: <span>{searchResults.totalCount}</span></div>

        </div>
      )}

    </div>
  );
};

export default Home;
